/**
 * Journal Sync Queue Service
 * 
 * Handles reliable background synchronization of voice journals with:
 * - Automatic retry with exponential backoff
 * - Persistent queue (localStorage)
 * - Network status awareness
 * - Batch processing for efficiency
 */

import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';

export interface QueuedJournalSync {
  id: string;
  userId: string;
  personId: string;
  contactEmail: string;
  journalId: string;
  createdAt: string;
  retryCount: number;
  lastAttempt?: string;
  error?: string;
}

class JournalSyncQueue {
  private queue: QueuedJournalSync[] = [];
  private isProcessing = false;
  private readonly STORAGE_KEY = 'journal_sync_queue';
  private readonly MAX_RETRIES = 5;
  private readonly INITIAL_BACKOFF = 1000; // 1 second
  private readonly MAX_BACKOFF = 60000; // 1 minute
  private processingTimer?: NodeJS.Timeout;

  constructor() {
    this.loadQueue();
    this.startProcessing();
    
    // Listen for online event to process queue
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('🌐 Network online, processing sync queue');
        this.processQueue();
      });
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueue(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.queue = safeJsonParse(stored);
        console.log(`📥 Loaded ${this.queue.length} items from sync queue`);
      }
    } catch (error) {
      console.error('Failed to load sync queue:', error);
      this.queue = [];
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueue(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save sync queue:', error);
    }
  }

  /**
   * Add a journal sync to the queue
   */
  add(sync: Omit<QueuedJournalSync, 'retryCount' | 'lastAttempt'>): void {
    const queuedSync: QueuedJournalSync = {
      ...sync,
      retryCount: 0,
    };

    this.queue.push(queuedSync);
    this.saveQueue();
    
    console.log(`➕ Added journal sync to queue: ${sync.journalId}`);
    
    // Try to process immediately
    this.processQueue();
  }

  /**
   * Remove a sync from the queue
   */
  remove(syncId: string): void {
    this.queue = this.queue.filter(s => s.id !== syncId);
    this.saveQueue();
    console.log(`➖ Removed journal sync from queue: ${syncId}`);
  }

  /**
   * Calculate backoff delay with exponential increase
   */
  private calculateBackoff(retryCount: number): number {
    const exponentialDelay = this.INITIAL_BACKOFF * Math.pow(2, retryCount);
    return Math.min(exponentialDelay, this.MAX_BACKOFF);
  }

  /**
   * Check if network is available
   */
  private isNetworkAvailable(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine;
  }

  /**
   * Process a single sync item
   */
  private async processSyncItem(sync: QueuedJournalSync): Promise<boolean> {
    try {
      console.log(`🔄 Processing sync: ${sync.journalId} (attempt ${sync.retryCount + 1}/${this.MAX_RETRIES})`);
      
      const token = blink.auth.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('https://ben3ou87--contact-intelligence.functions.blink.new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'sync_timeline',
          userId: sync.userId,
          personId: sync.personId,
          contactEmail: sync.contactEmail,
          journalId: sync.journalId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Sync failed');
      }

      console.log(`✅ Sync completed successfully: ${sync.journalId}`);
      return true;
    } catch (error: any) {
      console.error(`❌ Sync failed for ${sync.journalId}:`, error);
      
      // Update retry count and error
      sync.retryCount++;
      sync.lastAttempt = new Date().toISOString();
      sync.error = error.message || 'Unknown error';
      
      // Check if we should retry
      if (sync.retryCount < this.MAX_RETRIES) {
        const backoffDelay = this.calculateBackoff(sync.retryCount);
        console.log(`⏳ Will retry ${sync.journalId} in ${backoffDelay}ms`);
        this.saveQueue();
        return false;
      } else {
        console.error(`❌ Max retries exceeded for ${sync.journalId}, removing from queue`);
        return false;
      }
    }
  }

  /**
   * Process all items in the queue
   */
  async processQueue(): Promise<void> {
    // Prevent concurrent processing
    if (this.isProcessing) {
      console.log('⏸️ Queue already processing, skipping');
      return;
    }

    // Check network availability
    if (!this.isNetworkAvailable()) {
      console.log('📵 Network offline, skipping queue processing');
      return;
    }

    // No items to process
    if (this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    console.log(`🚀 Processing sync queue with ${this.queue.length} items`);

    const itemsToProcess = [...this.queue];
    const successfulSyncs: string[] = [];
    const failedSyncs: QueuedJournalSync[] = [];

    for (const sync of itemsToProcess) {
      // Check if item is ready for retry (respect backoff)
      if (sync.lastAttempt) {
        const lastAttemptTime = new Date(sync.lastAttempt).getTime();
        const backoffDelay = this.calculateBackoff(sync.retryCount - 1);
        const timeSinceLastAttempt = Date.now() - lastAttemptTime;
        
        if (timeSinceLastAttempt < backoffDelay) {
          console.log(`⏳ Skipping ${sync.journalId}, backoff period not elapsed`);
          failedSyncs.push(sync);
          continue;
        }
      }

      const success = await this.processSyncItem(sync);
      
      if (success) {
        successfulSyncs.push(sync.id);
      } else {
        // Keep in queue for retry if under max retries
        if (sync.retryCount < this.MAX_RETRIES) {
          failedSyncs.push(sync);
        }
      }

      // Small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Update queue with only failed items that haven't exceeded retries
    this.queue = failedSyncs;
    this.saveQueue();

    console.log(`✅ Queue processing complete. Success: ${successfulSyncs.length}, Remaining: ${this.queue.length}`);
    
    this.isProcessing = false;
  }

  /**
   * Start periodic queue processing
   */
  private startProcessing(): void {
    // Process queue every 30 seconds
    this.processingTimer = setInterval(() => {
      this.processQueue();
    }, 30000);

    // Process immediately on startup
    setTimeout(() => {
      this.processQueue();
    }, 2000);
  }

  /**
   * Stop periodic processing
   */
  stopProcessing(): void {
    if (this.processingTimer) {
      clearInterval(this.processingTimer);
      this.processingTimer = undefined;
    }
  }

  /**
   * Get queue status
   */
  getStatus(): {
    queueLength: number;
    processing: boolean;
    items: QueuedJournalSync[];
  } {
    return {
      queueLength: this.queue.length,
      processing: this.isProcessing,
      items: [...this.queue],
    };
  }

  /**
   * Clear the entire queue
   */
  clear(): void {
    this.queue = [];
    this.saveQueue();
    console.log('🗑️ Sync queue cleared');
  }
}

// Export singleton instance
export const journalSyncQueue = new JournalSyncQueue();
